import * as React from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Input as AriaInput,
  InputProps as AriaInputProps,
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { Button, ButtonProps } from '../buttons/Button';
import { FieldError, FieldGroup, Label } from '../Field';

export const NumberFieldInput = ({
  className,
  ...props
}: AriaInputProps & React.RefAttributes<HTMLInputElement>) => (
  <AriaInput
    className={composeRenderProps(
      cn('@NumberFieldInput', className),
      (className) =>
        cn(
          'w-fit min-w-0 flex-1 border-r border-transparent bg-space pr-2 outline-0',
          'placeholder:text-foreground/20 [&::-webkit-search-cancel-button]:hidden',
          className,
        ),
    )}
    {...props}
  />
);

export const NumberFieldSteppers = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    className={cn(
      '@NumberFieldSteppers',
      'absolute right-0 flex h-full flex-col border-l border-foreground/20',
      className,
    )}
    {...props}
  >
    <NumberFieldStepper slot="increment" icon={<ChevronUp aria-hidden />} />
    <div className="border-b border-foreground/20" />
    <NumberFieldStepper slot="decrement" icon={<ChevronDown aria-hidden />} />
  </div>
);

export const NumberFieldStepper = ({ className, ...props }: ButtonProps) => (
  <Button
    className={composeRenderProps(
      cn('@NumberFieldStepper', className),
      (className) =>
        cn('size-4 w-auto grow rounded-none text-foreground/20', className),
    )}
    variant="text"
    size="icon"
    {...props}
  />
);

export type NumberFieldProps = AriaNumberFieldProps & {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  showSteppers?: boolean;
  ref?: React.RefObject<HTMLInputElement>;
};

export const NumberField = ({
  label,
  description,
  errorMessage,
  className,
  prefix,
  suffix,
  showSteppers = true,
  ref,
  ...props
}: NumberFieldProps) => (
  <AriaNumberField
    ref={ref}
    isInvalid={!!errorMessage}
    className={composeRenderProps(className, (className) =>
      cn('@NumberField', 'group flex flex-col gap-2', className),
    )}
    {...props}
  >
    {label && <Label>{label}</Label>}
    <FieldGroup prefix={prefix} suffix={suffix} isDisabled={props.isDisabled}>
      <AriaInput
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
        className={cn(
          'text-right selection:bg-current/20',
          showSteppers && 'mr-2',
        )}
      />
      {showSteppers && <NumberFieldSteppers />}
    </FieldGroup>
    {description && (
      <Text className="text-sm text-foreground/20" slot="description">
        {description}
      </Text>
    )}
    <FieldError>{errorMessage}</FieldError>
  </AriaNumberField>
);
